import React, { useState } from "react";
import { Ban, ShieldCheck, Check, RefreshCw } from "lucide-react";
import { Button } from "../shared/Button";
import { restricciones as api } from "../../api/modulos.service";
import { coincide, coincidePlaca } from "../../utils/texto";
import { estilos as est, Cabecera, Buscador, Vacio, Aviso, Ventana, Entrada, Seleccion, Area, useCarga } from "./base";

/**
 * PERSONAS Y VEHÍCULOS RESTRINGIDOS.
 *
 * **El guardia reporta; la administración aprueba.** Un reporte nace
 * `pendiente` y no alerta a nadie hasta que se confirma: vetar a alguien de
 * forma inmediata y permanente es demasiado poder para un turno de noche.
 *
 * Y nada se borra. Se levanta, con motivo. El historial de a quién se vetó y
 * quién lo levantó es lo que hace defendible la lista.
 */

const FORM_VACIO = { kind: "persona", name: "", plate: "", documentId: "", reason: "", severity: "aviso" };

const COLOR_ESTADO = { pendiente: "var(--warning)", activa: "var(--danger)", levantada: "var(--text-tertiary)" };

export default function Restricciones({ rol }) {
  const [busqueda, setBusqueda] = useState("");
  const [abierta, setAbierta] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const { datos, cargando, error: errorCarga, recargar } = useCarga(() => api.listar({ todos: "1" }), []);
  const lista = datos?.restrictions || [];
  const esAdmin = rol === "admin";

  const filtrados = busqueda
    ? lista.filter((r) =>
        // La placa aparte: se guarda sin guiones, así que «Z99-888» y
        // «Z99888» tienen que encontrar la misma restricción.
        coincidePlaca(r.plate, busqueda) ||
        [r.name, r.reason].filter(Boolean).some((c) => coincide(c, busqueda))
      )
    : lista;

  const cambiar = (c) => (e) => setForm({ ...form, [c]: e.target.value });

  const guardar = async (e) => {
    e.preventDefault();
    setError("");
    setGuardando(true);
    try {
      await api.reportar(form);
      setAbierta(false);
      setForm(FORM_VACIO);
      recargar();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.errors?.[0]?.msg || "No se pudo registrar");
    } finally {
      setGuardando(false);
    }
  };

  const levantar = async (r) => {
    const motivo = window.prompt("¿Por qué se levanta esta restricción?");
    if (!motivo) return;
    try {
      await api.levantar(r._id, { liftReason: motivo });
      recargar();
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo levantar");
    }
  };

  return (
    <div style={est.seccion}>
      <Cabecera
        titulo="Restricciones"
        subtitulo={
          esAdmin
            ? "Apruebe los reportes de la caseta para que alerten al registrar."
            : "Reporte a quien no debería entrar. La administración lo confirma."
        }
        accion="Reportar"
        alAccionar={() => setAbierta(true)}
        icono={Ban}
      />

      <Buscador valor={busqueda} alCambiar={setBusqueda} marcador="Buscar por nombre, placa o motivo…" />

      {error && <Aviso>{error}</Aviso>}

      {/* El error va EN LUGAR del estado vacío. «No hay restricciones» es una
          respuesta y «no se pudo cargar» es otra muy distinta —la segunda pide
          llamar a alguien—, y enseñadas juntas el guardia no sabe cuál es. */}
      {errorCarga ? (
        <Aviso>
          {errorCarga}
          <div style={{ marginTop: "var(--space-3)" }}>
            <Button size="sm" variant="outline" leftIcon={RefreshCw} onClick={recargar}>
              Reintentar
            </Button>
          </div>
        </Aviso>
      ) : cargando ? (
        <Vacio>Cargando…</Vacio>
      ) : filtrados.length === 0 ? (
        <Vacio>No hay restricciones registradas.</Vacio>
      ) : (
        <div style={est.rejilla}>
          {filtrados.map((r) => (
            <div key={r._id} style={{ ...est.tarjeta, borderColor: COLOR_ESTADO[r.status] }}>
              <h3 style={est.tarjetaTitulo}>
                {r.kind === "persona" ? r.name : r.plate}
              </h3>
              <div style={{ ...est.tarjetaDato, color: COLOR_ESTADO[r.status], fontWeight: 600 }}>
                {r.status === "pendiente" && "Esperando aprobación"}
                {r.status === "activa" && (r.severity === "bloqueo" ? "Acceso bloqueado" : "Alerta en caseta")}
                {r.status === "levantada" && "Levantada"}
              </div>
              <div style={est.tarjetaDato}>{r.reason}</div>
              {r.status === "levantada" && r.liftReason && (
                <div style={{ ...est.tarjetaDato, fontStyle: "italic" }}>Se levantó: {r.liftReason}</div>
              )}

              {esAdmin && (
                <div style={est.tarjetaAcciones}>
                  {r.status === "pendiente" && (
                    <>
                      <Button size="sm" variant="primary" leftIcon={Check}
                        onClick={() => api.aprobar(r._id, { severity: "aviso" }).then(recargar)}>
                        Aprobar como aviso
                      </Button>
                      <Button size="sm" variant="danger" leftIcon={Ban}
                        onClick={() => api.aprobar(r._id, { severity: "bloqueo" }).then(recargar)}>
                        Aprobar como bloqueo
                      </Button>
                    </>
                  )}
                  {r.status === "activa" && (
                    <Button size="sm" variant="ghost" leftIcon={ShieldCheck} onClick={() => levantar(r)}>
                      Levantar
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Ventana
        titulo="Reportar una restricción"
        abierta={abierta}
        alCerrar={() => setAbierta(false)}
        pie={
          <>
            <Button variant="ghost" onClick={() => setAbierta(false)}>Cancelar</Button>
            <Button variant="primary" onClick={guardar} loading={guardando}>Reportar</Button>
          </>
        }
      >
        {error && <Aviso>{error}</Aviso>}
        {!esAdmin && (
          <Aviso>
            El reporte no alerta en la caseta hasta que la administración lo apruebe.
          </Aviso>
        )}

        <Seleccion
          etiqueta="Qué se restringe"
          value={form.kind}
          onChange={cambiar("kind")}
          opciones={[
            { valor: "persona", etiqueta: "Una persona" },
            { valor: "vehiculo", etiqueta: "Un vehículo" },
          ]}
        />

        {form.kind === "persona" ? (
          <>
            <Entrada etiqueta="Nombre" value={form.name} onChange={cambiar("name")} required />
            <Entrada etiqueta="DUI (opcional)" value={form.documentId} onChange={cambiar("documentId")} />
          </>
        ) : (
          <Entrada
            etiqueta="Placa"
            value={form.plate}
            onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })}
            placeholder="P123-456"
            required
          />
        )}

        <Area
          etiqueta="Motivo (obligatorio)"
          value={form.reason}
          onChange={cambiar("reason")}
          placeholder="Qué pasó y por qué no debería entrar"
          required
        />

        {esAdmin && (
          <Seleccion
            etiqueta="Severidad"
            value={form.severity}
            onChange={cambiar("severity")}
            opciones={[
              { valor: "aviso", etiqueta: "Aviso — el guardia decide" },
              { valor: "bloqueo", etiqueta: "Bloqueo — no debe entrar" },
            ]}
          />
        )}
      </Ventana>
    </div>
  );
}
